/**
 * LoreemaClipboard — ad-hoc importer for the legacy "lorema-flow-clipboard"
 * format. Pasted into the Storyflow editor via Ctrl+V before our internal
 * sessionStorage clipboard is consulted.
 *
 * This file is intentionally self-contained so it can be deleted in one step
 * once the migration from the legacy tool is done — no imports from this
 * module live in the rest of the codebase beyond a single hook inside
 * StoryflowEditorPage.
 *
 * Conversion rules (match what tmp-export-graphs.js did for the big DB migration):
 *   - dialogue    → sfDialogue (speakerId/Name resolved via payload.character)
 *   - image       → sfImage with empty media fields (user re-attaches by hand)
 *   - timeskip    → sfTimeskip (ordinal timeValue → mode/hours/minutes)
 *   - contact     → sfIntroduce
 *   - relationship/premium → skipped; their value/tier absorbed into the
 *     incoming dialogue answer that points at them.
 *   - video/trigger/branch/nextflow/chapter/start → skipped entirely (user
 *     re-creates these by hand).
 *
 * Emoji tags like "Emoji freez" are replaced live with real unicode emoji via
 * the same map used by the batch npm script (_replace-emoji.js).
 */

const EMOJI_MAP = {
  'Emoji smile': '🙂',
  'Emoji angel': '😇',
  'Emoji tongue': '😛',
  'Emoji happy 1': '😊',
  'Emoji happy 2': '😄',
  'Emoji love 1': '🥰',
  'Emoji love': '❤️',
  'Emoji aristocrat': '🧐',
  'Emoji laugh 1': '😂',
  'Emoji laugh 2': '🤣',
  'Emoji hot': '🥵',
  'Emoji stars': '🤩',
  'Emoji heart': '💖',
  'Emoji dead': '💀',
  'Emoji roar': '🤬',
  'Emoji eyes 1': '👀',
  'Emoji cry 3': '😭',
  'Emoji cry 2': '😢',
  'Emoji cry 1': '🥲',
  'Emoji beg': '🥺',
  'Emoji crazy': '🤪',
  'Emoji wink': '😉',
  'Emoji surprise': '😲',
  'Emoji kiss 2': '😘',
  'Emoji kiss 1': '💋',
  'Emoji disgust': '🤢',
  'Emoji think': '🤔',
  'Emoji with glasses': '😎',
  'Emoji sick 1': '🤒',
  'Emoji sad': '😔',
  'Emoji roll eyes': '🙄',
  'Emoji like': '👍',
  'Emoji devil 1': '😈',
  'Emoji devil': '👿',
  'Emoji amaze': '😮',
  'Emoji freez': '🥶',
  'Emoji angry': '😡',
  'Emoji drunk': '🥴',
  'Emoji eyes 2': '😏',
  'Emoji money': '🤑',
  'Emoji poo': '💩',
  'Emoji sleep 1': '😴'
};

// Sort by length (longest first) so "Emoji love 1" matches before "Emoji love".
const EMOJI_ENTRIES = Object.entries(EMOJI_MAP)
  .sort((a, b) => b[0].length - a[0].length)
  .map(([tag, emoji]) => ({
    tag,
    emoji,
    regex: new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'g')
  }));

function replaceEmojis(text) {
  if (!text) return text || '';
  let out = String(text);
  for (const { regex, emoji } of EMOJI_ENTRIES) {
    out = out.replace(regex, emoji);
  }
  return out;
}

// Old timeskip ordinal → new {mode, hours, minutes}. Copied verbatim from
// tmp-export-graphs.js so live paste and the original DB migration behave
// identically.
const TIMESKIP_MAP = [
  { mode: 'skip', hours: 0, minutes: 1 },   // 0: few moments
  { mode: 'skip', hours: 0, minutes: 5 },   // 1: 5 min
  { mode: 'skip', hours: 0, minutes: 10 },  // 2: 10 min
  { mode: 'skip', hours: 0, minutes: 15 },  // 3: 15 min
  { mode: 'skip', hours: 0, minutes: 20 },  // 4: 20 min
  { mode: 'skip', hours: 0, minutes: 30 },  // 5: 30 min
  { mode: 'skip', hours: 1, minutes: 0 },   // 6: 1 hour
  { mode: 'skip', hours: 2, minutes: 0 },   // 7: 2 hours
  { mode: 'skip', hours: 3, minutes: 0 },   // 8: 3 hours
  { mode: 'set',  hours: 10, minutes: 30 }, // 9: next morning
  { mode: 'set',  hours: 14, minutes: 0 }   // 10: next day
];

function findCharacter(query, characters) {
  if (!query || !Array.isArray(characters)) return { characterId: '', characterName: '' };
  const q = String(query).toLowerCase();
  for (const c of characters) {
    const name = String(c.name || '').toLowerCase();
    if (name.startsWith(q)) {
      return { characterId: c.id || c.characterId || '', characterName: c.name || '' };
    }
  }
  return { characterId: '', characterName: '' };
}

let _uidCounter = 0;
function newNodeId() {
  _uidCounter += 1;
  return `n-${Date.now()}-${_uidCounter}-${Math.random().toString(36).slice(2, 9)}`;
}
function newEdgeId() {
  _uidCounter += 1;
  return `e-${Date.now()}-${_uidCounter}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Detect whether a parsed JSON object is a Lorema clipboard payload. */
export function isLoremaClipboard(payload) {
  return !!(
    payload &&
    typeof payload === 'object' &&
    payload.clipboard &&
    payload.clipboard.type === 'lorema-flow-clipboard' &&
    Array.isArray(payload.clipboard.nodes)
  );
}

/** Parse a raw clipboard string. Returns the payload or null if not Lorema. */
export function parseLoremaClipboardText(text) {
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    return isLoremaClipboard(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

/**
 * Convert a Lorema clipboard payload into {nodes, edges} in our sfNode format.
 * Positions are shifted so the cluster's center lands at targetFlowPos (React
 * Flow coordinates). Unknown / explicitly-skipped node types are dropped and
 * edges through them are short-circuited.
 */
export function importLoremaClipboard(payload, { characters, targetFlowPos } = {}) {
  if (!isLoremaClipboard(payload)) return { nodes: [], edges: [] };
  const clip = payload.clipboard;
  const srcNodes = clip.nodes || [];
  const srcEdges = clip.edges || [];

  const speakerInfo = findCharacter(payload.character, characters);
  const speakerId = speakerInfo.characterId;
  const speakerName = speakerInfo.characterName;

  const srcNodeMap = new Map(srcNodes.map((n) => [n.id, n]));

  // Outgoing-edge index for the source graph
  const outgoing = new Map();
  for (const e of srcEdges) {
    if (!outgoing.has(e.source)) outgoing.set(e.source, []);
    outgoing.get(e.source).push(e);
  }

  // Decide which nodes are structurally dropped (short-circuited through).
  // relationship/premium → dropped, value absorbed into incoming dialogue.
  // video/trigger/branch/nextflow/chapter/start → dropped, user recreates.
  const skippedNodes = new Set();
  for (const n of srcNodes) {
    const t = n.type;
    if (
      t === 'relationship' ||
      t === 'premium' ||
      t === 'video' ||
      t === 'trigger' ||
      t === 'branch' ||
      t === 'nextflow' ||
      t === 'chapter' ||
      t === 'start'
    ) {
      skippedNodes.add(n.id);
    }
  }

  const idMap = new Map(); // old id → new id
  const newNodes = [];

  // PASS 1: create new nodes for non-skipped source nodes
  for (const n of srcNodes) {
    if (skippedNodes.has(n.id)) continue;
    const pos = { x: Number(n.position?.x) || 0, y: Number(n.position?.y) || 0 };
    const nid = newNodeId();
    idMap.set(n.id, nid);

    switch (n.type) {
      case 'dialogue': {
        const d = n.data || {};
        const answers = Array.isArray(d.answers) ? d.answers : [];
        const relationships = answers.map(() => 0);
        const tiers = answers.map(() => '');

        // Absorb relationship/premium nodes wired to each answer output
        const outs = outgoing.get(n.id) || [];
        for (let i = 0; i < answers.length; i++) {
          const ansOuts = outs.filter((o) => o.sourceHandle === `answer-${i}`);
          for (const ae of ansOuts) {
            const t = srcNodeMap.get(ae.target);
            if (t && t.type === 'relationship') {
              const v = Number(t.data?.value);
              if (!Number.isNaN(v)) relationships[i] = v;
            }
            if (t && t.type === 'premium') {
              tiers[i] = t.data?.premiumTier || t.data?.tier || '';
            }
          }
        }

        newNodes.push({
          id: nid,
          type: 'sfDialogue',
          position: pos,
          data: {
            speakerId,
            speakerName,
            text: replaceEmojis(d.text || ''),
            text_en: replaceEmojis(d.enText || ''),
            variants: answers.map((a) => replaceEmojis(a?.text || '')),
            variants_en: answers.map((a) => replaceEmojis(a?.enText || '')),
            relationships,
            tiers
          }
        });
        break;
      }

      case 'image': {
        const d = n.data || {};
        newNodes.push({
          id: nid,
          type: 'sfImage',
          position: pos,
          data: {
            label: 'Image',
            imageName: '',
            imageUrl: '',
            imagePreview512: '',
            playerSends: !!d.playerSends
          }
        });
        break;
      }

      case 'timeskip': {
        const d = n.data || {};
        const tv = Number(d.timeValue);
        const idx = Number.isFinite(tv) ? Math.max(0, Math.min(TIMESKIP_MAP.length - 1, tv)) : 0;
        const m = TIMESKIP_MAP[idx];
        newNodes.push({
          id: nid,
          type: 'sfTimeskip',
          position: pos,
          data: {
            label: 'Timeskip',
            timeskipMode: m.mode,
            hours: m.hours,
            minutes: m.minutes
          }
        });
        break;
      }

      case 'contact': {
        const d = n.data || {};
        const contactName = d.contactName || d.name || '';
        // Contacts in lorema clipboards usually carry a full name; fall back
        // to first-3-letters matching like the payload-level character field.
        const resolved =
          findCharacter(contactName, characters).characterId
            ? findCharacter(contactName, characters)
            : findCharacter(String(contactName).slice(0, 3), characters);
        newNodes.push({
          id: nid,
          type: 'sfIntroduce',
          position: pos,
          data: {
            label: 'Introduce',
            characterId: resolved.characterId,
            characterName: resolved.characterName
          }
        });
        break;
      }

      default:
        // Unknown type — pretend it was skipped so edges route through it
        idMap.delete(n.id);
        skippedNodes.add(n.id);
        break;
    }
  }

  // Walk through skipped nodes to find the next real target (BFS).
  function resolveTargets(startId) {
    const seen = new Set();
    const queue = [startId];
    const out = [];
    while (queue.length > 0) {
      const id = queue.shift();
      if (seen.has(id)) continue;
      seen.add(id);
      if (!skippedNodes.has(id)) {
        out.push(id);
        continue;
      }
      for (const e of outgoing.get(id) || []) queue.push(e.target);
    }
    return out;
  }

  function mapSourceHandle(sh) {
    if (sh && typeof sh === 'string' && sh.startsWith('answer-')) {
      return 'choice-' + sh.slice('answer-'.length);
    }
    return 'flow-out';
  }

  // PASS 2: build new edges
  const newEdges = [];
  const seen = new Set();
  for (const n of srcNodes) {
    if (skippedNodes.has(n.id)) continue;
    const sourceNew = idMap.get(n.id);
    if (!sourceNew) continue;
    for (const e of outgoing.get(n.id) || []) {
      const sourceHandle = mapSourceHandle(e.sourceHandle);
      const targets = resolveTargets(e.target);
      for (const targetOld of targets) {
        const targetNew = idMap.get(targetOld);
        if (!targetNew) continue;
        const targetHandle = 'flow-in';
        const key = `${sourceNew}::${sourceHandle}::${targetNew}::${targetHandle}`;
        if (seen.has(key)) continue;
        seen.add(key);
        newEdges.push({
          id: newEdgeId(),
          source: sourceNew,
          sourceHandle,
          target: targetNew,
          targetHandle,
          type: 'bleu',
          animated: true
        });
      }
    }
  }

  // Shift positions so the cluster's centre lands at targetFlowPos.
  if (targetFlowPos && newNodes.length > 0) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of newNodes) {
      const x = n.position.x;
      const y = n.position.y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + 300 > maxX) maxX = x + 300;
      if (y + 200 > maxY) maxY = y + 200;
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const dx = targetFlowPos.x - cx;
    const dy = targetFlowPos.y - cy;
    for (const n of newNodes) {
      n.position = { x: n.position.x + dx, y: n.position.y + dy };
    }
  }

  for (const n of newNodes) n.selected = true;

  return { nodes: newNodes, edges: newEdges };
}
